/**
 * Analytics Tab content for the Admin Dashboard.
 *
 * Self-contained: fetches `/api/admin/analytics/summary` for the chosen
 * date range, renders the period summary card, and composes all of the
 * dashboard's "deep-dive" analytics sub-cards (engagement, streaks, XP,
 * phrases, badges, subscription, todos, timer, recent activity).
 *
 * Extracted from AdminDashboard.jsx for maintainability — that file was
 * over 2,100 lines and needed to be split. No behavior changes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download } from 'lucide-react';
import {
  EngagementAnalytics,
  StreakAnalytics,
  ChallengeAnalytics,
  XPAnalytics,
  PhraseAnalytics,
  BadgeAnalytics,
  SubscriptionAnalytics,
  RecentActivity,
  QuickActionsPanel,
  GuestAnalytics,
  TodoAnalytics,
  TimerAnalytics,
} from '@/components/admin/AnalyticsDashboard';
import { HistoricalMetricsCard } from '@/components/admin/HistoricalMetricsCard';
import { RecoveryCampaignsCard } from '@/components/admin/RecoveryCampaignsCard';
import { PromoSettingsCard } from '@/components/admin/PromoSettingsCard';
import { SupportLinksCard } from '@/components/admin/SupportLinksCard';
import { apiFetch } from '@/hooks/useFetch';

export function AnalyticsTabContent() {
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [summary, setSummary] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const { data } = await apiFetch(`/api/admin/analytics/summary?start_date=${startDate}&end_date=${endDate}`);
      if (data) setSummary(data);
    } catch (e) { console.error(e); }
    finally { setLoadingSummary(false); }
  }, [startDate, endDate]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/admin/analytics/summary?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/csv' },
        credentials: 'include',
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${startDate}_to_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  const presets = [
    { label: '7 days', days: 7 },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
    { label: 'All time', days: 3650 },
  ];

  return (
    <div className="space-y-6">
      {/* Date Range Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">From</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" data-testid="analytics-start-date" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">To</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" data-testid="analytics-end-date" />
            </div>
            <div className="flex gap-1">
              {presets.map(p => (
                <Button key={p.days} variant="outline" size="sm" onClick={() => {
                  setStartDate(new Date(Date.now() - p.days * 86400000).toISOString().split('T')[0]);
                  setEndDate(today);
                }}>{p.label}</Button>
              ))}
            </div>
            <div className="ml-auto flex gap-2">
              <Button variant="default" size="sm" onClick={handleExportCSV} disabled={exporting} data-testid="export-analytics-csv">
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Period Summary */}
      {summary && (
        <Card data-testid="period-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Period Summary: {summary.date_range?.start} to {summary.date_range?.end}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <div className="text-center p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                <div className="text-xl font-bold text-green-600">{summary.active_users}</div>
                <div className="text-xs text-gray-500">Active Users</div>
              </div>
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <div className="text-xl font-bold text-blue-600">{summary.new_signups}</div>
                <div className="text-xs text-gray-500">New Signups</div>
              </div>
              <div className="text-center p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
                <div className="text-xl font-bold text-indigo-600">{summary.total_views}</div>
                <div className="text-xs text-gray-500">Phrase Views</div>
              </div>
              <div className="text-center p-3 bg-cyan-50 dark:bg-cyan-950/30 rounded-lg">
                <div className="text-xl font-bold text-cyan-600">{summary.todos_created}</div>
                <div className="text-xs text-gray-500">Todos Created</div>
              </div>
              <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                <div className="text-xl font-bold text-emerald-600">{summary.todos_completed}</div>
                <div className="text-xs text-gray-500">Todos Done</div>
              </div>
              <div className="text-center p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                <div className="text-xl font-bold text-purple-600">{summary.timer_sessions}</div>
                <div className="text-xs text-gray-500">Timer Sessions</div>
              </div>
              <div className="text-center p-3 bg-pink-50 dark:bg-pink-950/30 rounded-lg">
                <div className="text-xl font-bold text-pink-600">{summary.timer_total_minutes}</div>
                <div className="text-xs text-gray-500">Focus Minutes</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <QuickActionsPanel />

      <PromoSettingsCard />

      <SupportLinksCard />

      <HistoricalMetricsCard />

      <RecoveryCampaignsCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementAnalytics />
        <GuestAnalytics />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StreakAnalytics />
        <ChallengeAnalytics />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <XPAnalytics />
        <PhraseAnalytics />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BadgeAnalytics />
        <SubscriptionAnalytics />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TodoAnalytics />
        <TimerAnalytics />
      </div>
      <RecentActivity />
    </div>
  );
}

export default AnalyticsTabContent;
